import { Link } from "@ui/Atoms/Link/Link";
import { IconArrowUpRight } from "@ui/Icons/IconArrowUpRight";
import { CityChips } from "@ui/Molecules/City/CityChips";
import { EmbedCard } from "@ui/Molecules/Embed/Card/EmbedCard";
import { Paginator } from "@ui/Molecules/Paginator/Paginator";
import { RecentRow } from "@ui/Molecules/Recent/RecentRow";
import { SocialChips } from "@ui/Molecules/Social/SocialChips";
import { AppShell } from "@ui/Organisms/Layout/AppShell";
import type { EmbedsHomeTemplateProps } from "./types";

export function EmbedsHomeTemplate({
	topBar,
	tabBar,
	newsletter,
	curator,
	translations: t,
	socialLinks,
	cities,
	recent,
	feature,
	embeds,
	pagination,
	mapHref,
	onNewsletter,
}: EmbedsHomeTemplateProps) {
	const curatorMeta = (
		<div className="flex flex-wrap items-center gap-2 text-[12.5px] font-semibold text-[var(--content-muted)] md:text-[13px]">
			{curator.host && (
				<>
					<span>{curator.host}</span>
					<span className="h-[3px] w-[3px] rounded-full bg-[var(--border-strong)]" />
				</>
			)}
			<span>
				<span
					className="font-extrabold text-[var(--content-primary)]"
					style={{ fontFamily: "var(--font-display)" }}
				>
					{curator.placeCount}
				</span>{" "}
				{t.placesLabel}
			</span>
			<span className="h-[3px] w-[3px] rounded-full bg-[var(--border-strong)]" />
			<span>
				{curator.cityCount} {t.citiesLabel}
			</span>
		</div>
	);

	return (
		<AppShell topBar={topBar} tabBar={tabBar}>
			<div className="mx-auto flex max-w-[1360px] flex-col gap-8 px-5 py-5 md:gap-14 md:px-10 md:py-11">
				<div className="grid items-start gap-6 md:grid-cols-[minmax(0,420px)_minmax(0,1fr)] md:gap-12">
					{feature && (
						<div className="hidden md:block">
							<EmbedCard
								{...feature}
								variant="feature"
								eyebrow={t.featureEyebrow}
								emptyLabel={t.emptyEmbedLabel}
							/>
						</div>
					)}

					<div className="flex flex-col gap-4 md:justify-between md:gap-7">
						<div className="flex flex-col gap-4">
							<div className="flex items-center gap-3.5 md:gap-4">
								{curator.logo && (
									<img
										src={curator.logo}
										alt={curator.name}
										className="h-[62px] w-[62px] shrink-0 rounded-[var(--radius-pill)] object-cover shadow-[var(--shadow-logo)] md:h-16 md:w-16"
									/>
								)}
								<div className="flex min-w-0 flex-col gap-1">
									{curatorMeta}
									<Link
										href={curator.aboutHref}
										className="text-[14px] font-bold text-[var(--accent-500)] md:text-[15px]"
									>
										{t.aboutLinkLabel}
									</Link>
								</div>
							</div>

							<h1
								className="m-0 max-w-[16ch] text-[26px] font-extrabold leading-[1.06] tracking-[-0.03em] md:text-[44px]"
								style={{ fontFamily: "var(--font-display)" }}
							>
								{t.headline}
							</h1>
							<p className="m-0 max-w-[56ch] text-[14px] leading-[1.55] text-[var(--content-secondary)] md:text-[17px]">
								{t.intro}{" "}
								<span className="font-bold text-[var(--content-primary)]">
									{t.introEmphasis}
								</span>
							</p>
						</div>

						<div className="flex flex-col gap-5">
							<SocialChips links={socialLinks} onNewsletter={onNewsletter} />

							{cities.length > 0 && (
								<div className="flex flex-col gap-2.5">
									<div className="text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--content-muted)]">
										{t.citiesEyebrow}
									</div>
									<div className="md:hidden">
										<CityChips cities={cities} layout="scroll" />
									</div>
									<div className="hidden md:block">
										<CityChips cities={cities} layout="wrap" />
									</div>
								</div>
							)}
						</div>
					</div>
				</div>

				{recent.length > 0 && (
					<RecentRow items={recent} label={t.recentEyebrow} />
				)}

				<div className="flex flex-col gap-4">
					<div className="flex items-end justify-between border-b border-[var(--border-strong)] pb-3.5">
						<div className="flex items-baseline gap-3">
							<h2
								className="m-0 text-[19px] font-extrabold tracking-[-0.025em] md:text-[26px]"
								style={{ fontFamily: "var(--font-display)" }}
							>
								{t.embedsTitle}
							</h2>
							<span className="hidden text-[14px] font-semibold text-[var(--content-faint)] md:inline">
								{t.embedsMeta}
							</span>
						</div>
						<Link
							href={mapHref}
							className="inline-flex items-center gap-1 text-[13px] font-bold text-[var(--accent-500)] md:text-[14px]"
						>
							{t.mapLinkLabel}
							<IconArrowUpRight size={13} color="var(--accent-500)" />
						</Link>
					</div>

					<div className="grid grid-cols-3 gap-x-2 gap-y-3.5 md:grid-cols-4 md:gap-x-6 md:gap-y-7">
						{embeds.map((embed) => (
							<EmbedCard
								key={embed.id}
								{...embed}
								variant="grid"
								emptyLabel={t.emptyEmbedLabel}
							/>
						))}
					</div>
					{pagination && <Paginator {...pagination} />}
				</div>
			</div>
			{newsletter}
		</AppShell>
	);
}
